import React, { useEffect, useState } from 'react';
import { Plus, LogOut, Car, Download, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { subscribeToVehicles, createVehicle, deleteVehicle } from '../services/vehicleService';
import { deleteVehicleImage } from '../services/storageService';
import { defaultVehicles } from '../data/defaultVehicles';
import VehicleList from '../components/admin/VehicleList';
import VehicleForm from '../components/admin/VehicleForm';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToVehicles((data) => {
      setVehicles(data);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setShowForm(true);
  };

  const handleDelete = async (vehicle) => {
    if (!window.confirm(`Delete "${vehicle.name}"? This cannot be undone.`)) return;

    try {
      if (vehicle.imageUrl) {
        await deleteVehicleImage(vehicle.imageUrl);
      }
      await deleteVehicle(vehicle.id);
    } catch {
      alert('Failed to delete vehicle.');
    }
  };

  const handleImportDefaults = async () => {
    if (!window.confirm('Import the default vehicle collection? This adds 7 vehicles with local image paths.')) return;

    setImporting(true);
    try {
      for (const vehicle of defaultVehicles) {
        await createVehicle(vehicle);
      }
    } catch {
      alert('Failed to import vehicles. Make sure Firestore is configured.');
    } finally {
      setImporting(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingVehicle(null);
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="min-h-screen bg-black">
      <header className="border-b border-white/10 bg-black/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gradient-bg flex items-center justify-center">
              <Car className="h-5 w-5 text-black" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold text-white">SharpCars Admin</h1>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white glass-card transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              View Site
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-red-400 glass-card transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="font-display text-2xl font-bold text-white">Vehicle Inventory</h2>
            <p className="text-gray-500 text-sm mt-1">
              {vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''} in database
            </p>
          </div>

          <div className="flex gap-3">
            {vehicles.length === 0 && (
              <button
                onClick={handleImportDefaults}
                disabled={importing}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-gray-300 glass-card hover:border-white/20 transition-all disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                {importing ? 'Importing...' : 'Import Defaults'}
              </button>
            )}
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary !py-2.5 !px-5 !rounded-xl !text-sm"
            >
              <Plus className="h-4 w-4" />
              Add Vehicle
            </button>
          </div>
        </div>

        <div className="glass-card rounded-2xl border border-white/10 p-6">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-10 h-10 border-2 border-[#3DED97] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <VehicleList vehicles={vehicles} onEdit={handleEdit} onDelete={handleDelete} />
          )}
        </div>

        <p className="text-center mt-8 text-sm text-gray-600">
          Vehicle images are stored in{' '}
          <a
            href="https://console.cloud.google.com/storage"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#3DED97] hover:underline"
          >
            Google Cloud Storage
          </a>
          . Upload new images when adding or editing vehicles.
        </p>
      </main>

      {showForm && (
        <VehicleForm
          vehicle={editingVehicle}
          onClose={handleCloseForm}
          onSaved={() => {}}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
